import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
    ValidatedAddressModel,
} from "@insite/client-framework/Types/ApiModels";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@MasterBluePrint/Overrides/Components/CustomerAddressFormFields";
import PiyoviAddressConfirmModal from "@MasterBluePrint/Overrides/Components/PiyoviAddressConfirmModal";
import {
    areValidatedAddressesEquivalent,
    buildMergedAddress,
    isPiyoviValidatable,
    toEnteredValidatedAddress,
    toPiyoviApiParameter,
} from "@MasterBluePrint/Overrides/Common/Utilities/piyoviAddress";
import { getAddressValidationResult } from "@MasterBluePrint/Services/PiyoviService";
import { piyoviSettingSelector } from "@MasterBluePrint/Store/Context/ContextSelector";
import join from "lodash/join";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

interface OwnProps<Address extends BillToModel | ShipToModel> {
    address: Address;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    saveButtonTextOverride?: string;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>, address: Address) => void;
    extendedStyles?: CustomerAddressFormStyles;
    /** Injected by the connected wrapper below — the Piyovi AVS admin setting. */
    enablePiyoviAvs?: boolean;
}

interface PiyoviConfirmState<Address extends BillToModel | ShipToModel> {
    enteredAddress: ValidatedAddressModel;
    suggestedAddress?: ValidatedAddressModel;
    hasSuggestion: boolean;
    pendingAddress: Address;
    pendingEvent: React.FormEvent<HTMLFormElement>;
}

interface State<Address extends BillToModel | ShipToModel> {
    address: Address;
    countryLookup?: CountryModel;
    formErrors: FormErrorsState;
    formSubmitAttempted: boolean;
    isSubmitting: boolean;
    isValidatingAddress: boolean;
    piyoviConfirm?: PiyoviConfirmState<Address>;
}

type FormErrorsState = {
    firstName?: React.ReactNode;
    lastName?: React.ReactNode;
    attention?: React.ReactNode;
    companyName?: React.ReactNode;
    address1?: React.ReactNode;
    address2?: React.ReactNode;
    address3?: React.ReactNode;
    address4?: React.ReactNode;
    country?: React.ReactNode;
    city?: React.ReactNode;
    state?: React.ReactNode;
    postalCode?: React.ReactNode;
    phone?: React.ReactNode;
    email?: React.ReactNode;
    fax?: React.ReactNode;
};

export interface CustomerAddressFormStyles {
    form?: InjectableCss;
    formFields?: CustomerAddressFormFieldStyles;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const customerAddressFormStyles: CustomerAddressFormStyles = {
    formButtonsWrapper: {
        css: css`
            display: flex;
            box-shadow: 0 -2px 6px 0 rgba(0,0,0,0.31);
            background-color: #fff;
            margin-top: 1.875rem;
            padding-bottom: 1.875rem;
            padding-top: 1.875rem;
            position: fixed;
            width: 570px;
            left: auto;
            bottom: 0px;
            right: 0;
            border-radius: 1px;

            > div:last-child {
                flex-direction: row;
                max-width: 70%;
                margin: 0 auto;
                display: flex;

                > button[data-test-selector="customerAddressForm_save"] {
                    margin-bottom: 0;
                }
            }

            @media screen and (max-width: 767px) {
                &:last-child {
                    width: 100%;
                    box-shadow: none;

                    > div:last-child {
                        max-width: 80%;
        
                        > button[data-test-selector="customerAddressForm_save"] {
                            margin-bottom: 0;
                        }
                    }

                    > button[data-test-selector="customerAddressForm_save"],
                    button[data-test-selector="customerAddressForm_save"] {
                        margin-bottom: 0;
                    }
                    
                }
            }
        `,
    },
    submitButton: {
        css: css`
            height: 40px;
            border-radius: 2px;
            background: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;
            width: auto;
            padding: 0 50px;
            margin-bottom: 0px;
            white-space: nowrap;

            @media screen and (max-width: 767px) {
                padding: 0 30px;
            }

            &:hover,
             &:focus {
                color: #fff;
                background-color: #002554 !important;
                border-color: #002554 !important;
            }
        `,
    },
    cancelButton: {
        css: css`
            color: #868686;
            background: transparent;
            border: 2px solid transparent;
            font-family: Roboto;
            font-size: 15px;
            letter-spacing: 0;
            line-height: 21px;
            text-align: center;
            text-decoration: underline;
            margin-right: 60px;
            padding: 0 !important;

            @media screen and (max-width: 767px) {
                margin-right: 30px;
            }

            > span {
                font-weight: normal;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554;
                background: transparent;
                border: 2px solid transparent;
                padding: 0 !important;
            }
            
        `,
    },
};

const styles = customerAddressFormStyles;
const StyledForm = getStyledWrapper("form");

const getRequiredErrorMessage = (fieldDisplay: AddressFieldDisplayModel) =>
    siteMessage("Field_Required", fieldDisplay.displayName);

class CustomerAddressFormClass<Address extends BillToModel | ShipToModel> extends React.Component<
    OwnProps<Address>,
    State<Address>
> {
    private styles: CustomerAddressFormStyles;


    constructor(props: OwnProps<Address>) {
        super(props);
        this.styles = mergeToNew(styles, props.extendedStyles);
        const { address } = this.props;

        const countryLookup = this.props.countries.find(c => c.id === address.country?.id);
        this.state = {
            address,
            countryLookup,
            formErrors: {},
            formSubmitAttempted: false,
            isSubmitting: false,
            isValidatingAddress: false,
            piyoviConfirm: undefined,
        };
    }

    componentDidUpdate(_: OwnProps<Address>, prevState: State<Address>) {
        if (prevState.address !== this.state.address && this.state.formSubmitAttempted) {
            this.validateForm();
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;
        this.setState({
            address: {
                ...this.state.address,
                [propertyName]: target.value,
            },
        });
    };

    handleSetShipTo = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        this.setState({
            address: {
                ...this.state.address,
                isDefault: value,
            },
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const countryLookup = this.props.countries.find(c => c.id === country?.id);
        this.setState({
            address: {
                ...this.state.address,
                country,
                state: null,
            },
            countryLookup,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) =>
        this.setState({
            address: {
                ...this.state.address,
                state,
            },
        });

    validateForm = () => {
        const fieldDisplay = this.props.addressFieldDisplayCollection;
        const validation = this.props.address.validation!;
        const { countries } = this.props;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            isDefault,
        } = this.state.address;

        const formErrors: FormErrorsState = {};
        if (fieldDisplay.firstName?.isVisible && firstName.trim().length <= 0) {
            formErrors.firstName = getRequiredErrorMessage(fieldDisplay.firstName);
        }

        if (fieldDisplay.lastName?.isVisible && lastName.trim().length <= 0) {
            formErrors.lastName = getRequiredErrorMessage(fieldDisplay.lastName);
        }

        if (fieldDisplay.attention?.isVisible && validation.attention?.isRequired && attention.trim().length <= 0) {
            formErrors.attention = getRequiredErrorMessage(fieldDisplay.attention);
        }

        if (
            fieldDisplay.companyName?.isVisible &&
            validation.companyName?.isRequired &&
            companyName.trim().length <= 0
        ) {
            formErrors.companyName = getRequiredErrorMessage(fieldDisplay.companyName);
        }

        if (fieldDisplay.address1?.isVisible && validation.address1?.isRequired && address1.trim().length <= 0) {
            formErrors.address1 = getRequiredErrorMessage(fieldDisplay.address1);
        }

        if (fieldDisplay.address2?.isVisible && validation.address2?.isRequired && address2.trim().length <= 0) {
            formErrors.address2 = getRequiredErrorMessage(fieldDisplay.address2);
        }

        // if (fieldDisplay.address3?.isVisible && validation.address3?.isRequired && address3.trim().length <= 0) {
        //     formErrors.address3 = getRequiredErrorMessage(fieldDisplay.address3);
        // }

        // if (fieldDisplay.address4?.isVisible && validation.address4?.isRequired && address4.trim().length <= 0) {
        //     formErrors.address4 = getRequiredErrorMessage(fieldDisplay.address4);
        // }

        if (countries.length > 1 && !country && fieldDisplay.country) {
            formErrors.country = getRequiredErrorMessage(fieldDisplay.country);
        }

        if (fieldDisplay.city?.isVisible && validation.city?.isRequired && city.trim().length <= 0) {
            formErrors.city = getRequiredErrorMessage(fieldDisplay.city);
        }

        const { countryLookup } = this.state;
        if (
            countries.length > 0 &&
            countryLookup?.states &&
            countryLookup.states.length > 0 &&
            !state &&
            fieldDisplay.state
        ) {
            formErrors.state = getRequiredErrorMessage(fieldDisplay.state);
        }

        if (fieldDisplay.postalCode?.isVisible && validation.postalCode?.isRequired && postalCode.trim().length <= 0) {
            formErrors.postalCode = getRequiredErrorMessage(fieldDisplay.postalCode);
        }

        // Define individual parts of the fax regex
        const digitPart = '\\d';
        const optionalExtPart = '\\s?(ext)?';
        const surroundingChars = '[\\(\\)/\\-\\.\\+\\s]*';
        const faxPattern = `^(${surroundingChars}${digitPart}${optionalExtPart}${surroundingChars}){10,}$`;

        // Use Lodash to create the final fax regex pattern
        const faxRegexp = new RegExp(join([faxPattern], ''));

        if (fieldDisplay.phone?.isVisible && validation.phone?.isRequired && phone.trim().length <= 0) {
            formErrors.phone = getRequiredErrorMessage(fieldDisplay.phone);
        } else if (phone.trim().length > 0 && !faxRegexp.test(phone)) {
            formErrors.phone = siteMessage("AddressInfo_Phone_Validation");
        }

        if (fieldDisplay.email?.isVisible && email.trim().length <= 0) {
            formErrors.email = getRequiredErrorMessage(fieldDisplay.email);
        } else if (email.trim().length > 0 && !validateEmail(email)) {
            formErrors.email = siteMessage("AddressInfo_EmailAddress_Validation");
        }

        if (fieldDisplay.fax?.isVisible && validation.fax?.isRequired && fax.trim().length <= 0) {
            formErrors.fax = getRequiredErrorMessage(fieldDisplay.fax);
        } else if (fax.trim().length > 0 && !faxRegexp.test(fax)) {
            formErrors.fax = siteMessage("AddressInfo_Phone_Validation");
        }

        this.setState({ formErrors });

        return formErrors;
    };

    submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ formSubmitAttempted: true, isSubmitting: true });

        const formErrors = this.validateForm();

        this.setState({ formErrors }, () => {
            if (Object.keys(this.state.formErrors).length === 0) {
                // Local field validation passed — run Piyovi AVS before saving.
                void this.runPiyoviValidation(event, { ...this.props.address, ...this.state.address });
            } else {
                this.setState({
                    isSubmitting: false,
                });
            }
        });
    };

    // Saves the address (the original onSubmit behavior), used once AVS is resolved.
    finalizeSubmit = (event: React.FormEvent<HTMLFormElement>, address: Address) => {
        this.props.onSubmit(event, address);
    };

    // Runs Piyovi validation, then either saves directly or opens the confirmation modal.
    runPiyoviValidation = async (event: React.FormEvent<HTMLFormElement>, address: Address) => {
        // AVS off, or address not validatable (missing required fields / non-US-CA) → save as-is.
        if (!this.props.enablePiyoviAvs || !isPiyoviValidatable(address)) {
            this.finalizeSubmit(event, address);
            return;
        }

        this.setState({ isValidatingAddress: true });
        const result = await getAddressValidationResult(toPiyoviApiParameter(address));
        this.setState({ isValidatingAddress: false });

        // Verified as entered → nothing to confirm, save as-is.
        if (result.isSuccess && result.isSameAddress) {
            this.finalizeSubmit(event, address);
            return;
        }

        // Suggestion only differs by case/whitespace/punctuation → effectively the
        // same address, nothing meaningful to confirm, save as-is.
        if (
            result.isSuccess &&
            result.hasSuggestion &&
            result.validatedAddress &&
            areValidatedAddressesEquivalent(toEnteredValidatedAddress(address), result.validatedAddress)
        ) {
            this.finalizeSubmit(event, address);
            return;
        }

        // Otherwise prompt: Mode A (suggested vs entered) or Mode B (unverifiable / technical).
        this.setState({
            piyoviConfirm: {
                enteredAddress: toEnteredValidatedAddress(address),
                suggestedAddress: result.hasSuggestion ? result.validatedAddress : undefined,
                hasSuggestion: result.hasSuggestion,
                pendingAddress: address,
                pendingEvent: event,
            },
        });
    };

    handlePiyoviUseSuggested = () => {
        const confirm = this.state.piyoviConfirm;
        if (!confirm) {
            return;
        }
        const address = confirm.suggestedAddress
            ? buildMergedAddress(confirm.pendingAddress, confirm.suggestedAddress)
            : confirm.pendingAddress;
        this.setState({ piyoviConfirm: undefined });
        this.finalizeSubmit(confirm.pendingEvent, address);
    };

    handlePiyoviKeepEntered = () => {
        const confirm = this.state.piyoviConfirm;
        if (!confirm) {
            return;
        }
        this.setState({ piyoviConfirm: undefined });
        this.finalizeSubmit(confirm.pendingEvent, confirm.pendingAddress);
    };

    handlePiyoviEdit = () => {
        // Close the confirmation modal only; leave the form open for editing.
        this.setState({ piyoviConfirm: undefined, isSubmitting: false });
    };


    render() {
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            isDefault,
        } = this.state.address;
        const { formErrors } = this.state;
        const { countries, extendedStyles } = this.props;

        const { validation } = this.props.address;
        const fieldDisplay = this.props.addressFieldDisplayCollection;

        if (!validation) {
            return null;
        }

        const tempAddress = this.props.address as ShipToModel;
        const { piyoviConfirm } = this.state;

        return (
            <>
            <StyledForm {...styles.form} onSubmit={this.submitHandler} noValidate>
                <CustomerAddressFormFields
                    firstName={firstName}
                    lastName={lastName}
                    attention={attention}
                    companyName={companyName}
                    address1={address1}
                    address2={address2}
                    address3={address3}
                    address4={address4}
                    country={country}
                    city={city}
                    state={state}
                    postalCode={postalCode}
                    phone={phone}
                    email={email}
                    fax={fax}
                    isDefault={isDefault}
                    onChangeFirstName={this.handleInputChange}
                    onChangeLastName={this.handleInputChange}
                    onChangeAttention={this.handleInputChange}
                    onChangeCompanyName={this.handleInputChange}
                    onChangeAddress1={this.handleInputChange}
                    onChangeAddress2={this.handleInputChange}
                    onChangeAddress3={this.handleInputChange}
                    onChangeAddress4={this.handleInputChange}
                    onChangeCountry={this.handleCountryChange}
                    onChangeState={this.handleStateChange}
                    onChangeCity={this.handleInputChange}
                    onChangePostalCode={this.handleInputChange}
                    onChangePhone={this.handleInputChange}
                    onChangeEmail={this.handleInputChange}
                    onChangeFax={this.handleInputChange}
                    setShipTo={this.handleSetShipTo}
                    firstNameError={formErrors.firstName}
                    lastNameError={formErrors.lastName}
                    attentionError={formErrors.attention}
                    companyNameError={formErrors.companyName}
                    address1Error={formErrors.address1}
                    address2Error={formErrors.address2}
                    address3Error={formErrors.address3}
                    address4Error={formErrors.address4}
                    countryError={formErrors.country}
                    stateError={formErrors.state}
                    cityError={formErrors.city}
                    postalCodeError={formErrors.postalCode}
                    phoneError={formErrors.phone}
                    emailError={formErrors.email}
                    faxError={formErrors.fax}
                    countries={countries}
                    validation={validation}
                    fieldDisplay={fieldDisplay}
                    extendedStyles={extendedStyles?.formFields}
                />
                <StyledWrapper {...styles.formButtonsWrapper}>
                    <StyledWrapper>
                        {this.props.onCancel && (
                            <Button
                                {...this.styles.cancelButton}
                                type="button"
                                onClick={this.props.onCancel}
                                data-test-selector="customerAddressForm_cancel"
                            >
                                {translate("Cancel")}
                            </Button>
                        )}

                        {!tempAddress?.isNew ?
                            <Button
                                {...this.styles.submitButton}
                                type="submit"
                                disabled={this.state.isValidatingAddress}
                                data-test-selector="customerAddressForm_save"
                            >
                                {this.props.saveButtonTextOverride ?? translate("Update my Information")}
                            </Button> :
                            <Button
                                {...this.styles.submitButton}
                                type="submit"
                                disabled={this.state.isValidatingAddress}
                                data-test-selector="customerAddressForm_save"
                            >
                                {this.props.saveButtonTextOverride ?? translate("Save my Information")}
                            </Button>
                        }

                    </StyledWrapper>
                </StyledWrapper>
            </StyledForm>
            <PiyoviAddressConfirmModal
                isOpen={!!piyoviConfirm && !!this.props.enablePiyoviAvs}
                hasSuggestion={!!piyoviConfirm?.hasSuggestion && !!piyoviConfirm?.suggestedAddress}
                enteredAddress={piyoviConfirm?.enteredAddress}
                suggestedAddress={piyoviConfirm?.suggestedAddress}
                onUseSuggested={this.handlePiyoviUseSuggested}
                onKeepEntered={this.handlePiyoviKeepEntered}
                onEdit={this.handlePiyoviEdit}
            />
            </>)
    }
}

/**
 * Connected wrapper: injects the Piyovi AVS admin setting while preserving the
 * generic `Address` type param (react-redux `connect` would erase it). All call
 * sites keep importing this default export unchanged.
 */
const CustomerAddressForm = <Address extends BillToModel | ShipToModel>(props: OwnProps<Address>) => {
    const enablePiyoviAvs = useSelector((state: ApplicationState) => piyoviSettingSelector(state)?.enablePiyoviAvs);
    return <CustomerAddressFormClass<Address> {...props} enablePiyoviAvs={enablePiyoviAvs} />;
};

export default CustomerAddressForm;